import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';

import {
    BaseAdvancedSearchSandbox,
    ColumnFilterComponent,
    ColumnSortComponent,
    NavigationLinkDirective,
    ShActionItemComponent,
    ShFacetComponent,
    ShFacetListComponent,
    ShFacetPanelHeadDirective,
    ShFacetSummaryComponent,
    ShFormCheckboxComponent,
    ShFormCheckboxWithDropdownModule,
    ShFormMultiSelectModule,
    ShFormTextInputComponent,
    ShIconModule,
    ShListRowTitleComponent,
    ShNotificationComponent,
    ShPageHeaderComponent,
    ShPaginationComponent,
    ShProfileFacetListContainerComponent,
    ShSearchBarModule
} from '@stylehaven/components';

import { NgLetModule } from 'ng-let';
import { AutocompleteService, ReadableDatePipeModule } from '../../entities';
import { DigihavenElementsModule } from '../elements/elements.module';
import { LuceneHelper } from '../helpers/lucene.helper';
import { DigihavenSavedQueriesModule } from '../saved-queries/saved-queries.module';
import { ProfilesService } from '../services/profiles/profiles.service';
import { SavedQueriesService } from '../services/saved-queries/saved-queries.service';
import { OverviewOptionsService } from '../services/store/overview-options.service';
import { AdvancedSearchSandboxService } from './advanced-search/advanced-search.sandbox';
import { CellComponent } from './components/cell/cell.component';
import { CheckmarkCellComponent } from './components/cells/checkmark-cell/checkmark-cell.component';
import { CountCellComponent } from './components/cells/count-cell/count-cell.component';
import { FormatBadgeCellComponent } from './components/cells/format-badge-cell/format-badge-cell.component';
import { FormatPreviewLinkComponent } from './components/format-preview-link/format-preview-link.component';
import { IconCellComponent } from './components/cells/icon-cell/icon-cell.component';
import { ParentCellComponent } from './components/cells/parent-cell/parent-cell.component';
import { ShortDateCellComponent } from './components/cells/short-date-cell/short-date-cell.component';
import { ShortRecordIdCellComponent } from './components/cells/short-record-id-cell/short-record-id-cell.component';
import { StatusCellComponent } from './components/cells/status-cell/status-cell.component';
import { StukCountCellComponent } from './components/cells/stuk-count-cell/stuk-count-cell.component';
import { StukFormatsCellComponent } from './components/cells/stuk-formats-cell/stuk-formats-cell.component';
import { TextCellComponent } from './components/cells/text-cell/text-cell.component';
import { TitleCellComponent } from './components/cells/title-cell/title-cell.component';
import { DynamicComponent } from './components/dynamic/dynamic.component';
import { GenericTitleComponent } from './components/generic-title/generic-title.component';
import { ActiveFilterBarComponent } from './components/active-filter-bar/active-filter-bar.component';
import { SubmapTreeComponent } from './components/submap-tree/submap-tree.component';
import { SavedQueriesPanelComponent } from './components/saved-queries-panel/saved-queries-panel.component';
import { ExportSidepanelComponent } from '../export-sidepanel/export-sidepanel.component';
import { HierarchicalListComponent } from './components/hierarchical-list/hierarchical-list.component';
import { ListComponent } from './components/list/list.component';
import { OverviewSearchRowComponent } from './components/overview-search-row/overview-search-row.component';
import { RecordOverviewContainerComponent } from './containers/record-overview-container/record-overview-container.component';
import { RecordSearchBarContainerComponent } from './containers/record-search-bar/record-search-bar-container.component';
import { GetFieldPipe } from './pipes/get-field.pipe';
import { GetShortRecordIdPipe } from './pipes/get-short-record-id.pipe';
import { TranslateFieldDefinitionPipe } from './pipes/translate-field-definition.pipe';
import { RecordSandboxService } from './sandbox/record-sandbox.service';
import { QuickExportService } from '../downloads/services/quick-export.service';
import { QuickExportSandboxService } from '../downloads/sandbox/quick-export-sandbox.service';
import { ColumnHandlerService, RecordsColumnHandlerService } from './services/column-handler.service';
import { FacetValueTranslationService } from './services/facets/facet-value-translation.service';
import { FragmentService } from './services/fragment.service';
import { HierarchyService } from './services/hierarchy.service';
import { StukCountService } from './services/stuk-count.service';
import { StukFormatsService } from './services/stuk-formats.service';
import { RecordService } from './services/record.service';
import { RecordResolverService } from './services/resolver/record-resolver.service';
import { VersioningService } from './services/versioning.service';

@NgModule({
    declarations: [
        ListComponent,
        HierarchicalListComponent,
        RecordOverviewContainerComponent,
        RecordSearchBarContainerComponent,
        OverviewSearchRowComponent,
        GetFieldPipe,
        GetShortRecordIdPipe,
        TranslateFieldDefinitionPipe,
        CellComponent,
        DynamicComponent,
        TextCellComponent,
        IconCellComponent,
        StatusCellComponent,
        CheckmarkCellComponent,
        TitleCellComponent,
        ShortRecordIdCellComponent,
        ShortDateCellComponent,
        CountCellComponent,
        StukFormatsCellComponent,
        FormatBadgeCellComponent,
        StukCountCellComponent,
        ParentCellComponent,
        GenericTitleComponent
    ],
    imports: [
        CommonModule,
        DigihavenElementsModule,
        TranslateModule,
        ShActionItemComponent,
        ShSearchBarModule,
        ShFacetSummaryComponent,
        ShFacetListComponent,
        ShFacetComponent,
        ShIconModule,
        ShListRowTitleComponent,
        FormatPreviewLinkComponent,
        ColumnFilterComponent,
        ColumnSortComponent,
        ActiveFilterBarComponent,
        SubmapTreeComponent,
        ShNotificationComponent,
        ShPageHeaderComponent,
        ShPaginationComponent,
        DigihavenSavedQueriesModule,
        ReactiveFormsModule,
        ReadableDatePipeModule,
        ShFormMultiSelectModule,
        ShProfileFacetListContainerComponent,
        ShFormCheckboxWithDropdownModule,
        NavigationLinkDirective,
        NgLetModule,
        ShFormCheckboxComponent,
        ShFormTextInputComponent,
        ExportSidepanelComponent,
        SavedQueriesPanelComponent,
        ShFacetPanelHeadDirective
    ],
    exports: [
        // Re-exported so the three Taken pages can render the same bar the record overviews do.
        ActiveFilterBarComponent,
        ListComponent,
        HierarchicalListComponent,
        RecordSearchBarContainerComponent,
        RecordOverviewContainerComponent,
        OverviewSearchRowComponent,
        GetFieldPipe,
        GetShortRecordIdPipe,
        TranslateFieldDefinitionPipe,
        GenericTitleComponent
    ],
    providers: [
        RecordService,
        VersioningService,
        RecordSandboxService,
        QuickExportService,
        QuickExportSandboxService,
        HierarchyService,
        StukFormatsService,
        StukCountService,
        FragmentService,
        LuceneHelper,
        GetFieldPipe,
        GetShortRecordIdPipe,
        TranslateFieldDefinitionPipe,
        {
            provide: ColumnHandlerService,
            useClass: RecordsColumnHandlerService
        },
        SavedQueriesService,
        RecordResolverService,
        OverviewOptionsService,
        { provide: BaseAdvancedSearchSandbox, useClass: AdvancedSearchSandboxService },
        ProfilesService,
        FacetValueTranslationService,
        AutocompleteService
    ]
})
export class RecordModule {}
export * from './components/active-filter-bar/entities/active-filter-pill.entity';
export * from './components/cells/checkmark-cell/checkmark-cell.component';
export * from './components/cells/count-cell/count-cell.component';
export * from './components/cells/status-cell/status-cell.component';
export * from './components/generic-title/generic-title.component';
export * from './components/hierarchical-list/hierarchical-list.component';
export * from './containers/common/record-container';
export * from './containers/common/record-sandbox';
export * from './helpers/column-enum-facet.helper';
export * from './helpers/column-header-filters.helper';
export * from './helpers/dossier-type-facet.helper';
export * from './helpers/orphaned-sort-reset.helper';
export * from './helpers/overview-id.helper';
export * from './services/active-filter-state.service';
export * from './services/facet-order.service';
